import React from 'react';

/**
 * The Studio Playground night sky behind the song grid. Vector (SVG), so it stays sharp on Retina
 * screens and at any zoom, and it fills the visible grid instead of being stretched to the track
 * rows. Hidden when the grid look is "Solid black" (data-grid="black" on <html>).
 */
const TILE_W = 960;
const TILE_H = 640;

// Seeded so the sky is the same on every load (no flicker of a new random sky on re-render).
function seeded(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Colors are skin tokens (theme/tokens.css) so another skin can restyle the sky.
const TINTS = ['--ba-sky-star', '--ba-sky-star', '--ba-sky-star', '--ba-sky-star', '--ba-sky-star-cool', '--ba-sky-star-mint', '--ba-sky-star-violet'].map((name) => `var(${name})`);
const STARS = (() => {
  const random = seeded(1807);
  return Array.from({ length: 120 }, () => {
    const big = random() > 0.9;
    return {
      x: +(random() * TILE_W).toFixed(1),
      y: +(random() * TILE_H).toFixed(1),
      r: +(big ? 1.1 + random() * 0.6 : 0.45 + random() * 0.6).toFixed(2),
      o: +(big ? 0.55 + random() * 0.3 : 0.16 + random() * 0.38).toFixed(2),
      fill: TINTS[Math.floor(random() * TINTS.length)],
    };
  });
})();
const TWINKLES = STARS.filter((star) => star.r > 1.05).slice(0, 8);

const PLANETS = [
  { id: 'teal', x: '13%', y: '24%', r: 9 },
  { id: 'violet', x: '58%', y: '16%', r: 6 },
  { id: 'gold', x: '82%', y: '66%', r: 13, ring: true },
].map((planet) => ({ ...planet, light: `var(--ba-sky-${planet.id}-light)`, dark: `var(--ba-sky-${planet.id}-dark)`, glow: `var(--ba-sky-${planet.id}-glow)` }));

const Planet = ({ planet }) => {
  const { r } = planet;
  const ringX = r * 2.3; const ringY = r * 0.62;
  return (
    <svg x={planet.x} y={planet.y} overflow="visible">
      <circle r={r * 3.2} fill={`url(#ba-sky-glow-${planet.id})`} />
      <g transform="rotate(-18)">
        {planet.ring && <path d={`M ${-ringX} 0 A ${ringX} ${ringY} 0 0 1 ${ringX} 0`} fill="none" style={{ stroke: planet.light }} strokeOpacity="0.38" strokeWidth="1.4" />}
        <circle r={r} fill={`url(#ba-sky-body-${planet.id})`} />
        <circle r={r} fill="url(#ba-sky-shade)" />
        {planet.ring && <path d={`M ${ringX} 0 A ${ringX} ${ringY} 0 0 1 ${-ringX} 0`} fill="none" style={{ stroke: planet.light }} strokeOpacity="0.6" strokeWidth="1.4" />}
      </g>
    </svg>
  );
};

export const SkyBackdrop = ({ left = 0 }) => (
  <div className="ba-sky" data-testid="sky-backdrop" aria-hidden="true" style={{ left }}>
    <svg width="100%" height="100%">
      <defs>
        <pattern id="ba-sky-stars" width={TILE_W} height={TILE_H} patternUnits="userSpaceOnUse">
          {STARS.map((star, index) => <circle key={index} cx={star.x} cy={star.y} r={star.r} style={{ fill: star.fill }} fillOpacity={star.o} />)}
        </pattern>
        <radialGradient id="ba-sky-shade" cx="0.72" cy="0.74" r="0.85">
          <stop offset="0.35" style={{ stopColor: 'var(--ba-sky-shade)' }} stopOpacity="0" />
          <stop offset="1" style={{ stopColor: 'var(--ba-sky-shade)' }} stopOpacity="0.55" />
        </radialGradient>
        {PLANETS.map((planet) => (
          <React.Fragment key={planet.id}>
            <radialGradient id={`ba-sky-body-${planet.id}`} cx="0.34" cy="0.3" r="0.85">
              <stop offset="0" style={{ stopColor: planet.light }} />
              <stop offset="1" style={{ stopColor: planet.dark }} />
            </radialGradient>
            <radialGradient id={`ba-sky-glow-${planet.id}`}>
              <stop offset="0.25" style={{ stopColor: planet.glow }} />
              <stop offset="1" style={{ stopColor: planet.glow }} stopOpacity="0" />
            </radialGradient>
          </React.Fragment>
        ))}
      </defs>
      <rect width="100%" height="100%" fill="url(#ba-sky-stars)" />
      {TWINKLES.map((star, index) => (
        <circle key={index} className="ba-sky-twinkle" style={{ animationDelay: `${index * 0.9}s` }} cx={star.x} cy={star.y} r={star.r + 0.35} style={{ fill: 'var(--ba-sky-star)' }} />
      ))}
      {PLANETS.map((planet) => <Planet key={planet.id} planet={planet} />)}
    </svg>
  </div>
);

export default SkyBackdrop;
